import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useTriggerLoad } from "@redotech/react-util/load";
import AlertTriangleIcon from "@redotech/redo-icons/alert-triangle.svg";
import XClose from "@redotech/redo-icons/x-close.svg";
import {
  StorageLocationId,
  storageLocationIdFormat,
} from "@redotech/redo-warehouse-id";
import { toast } from "@redotech/redo-web/alert";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import type { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useEffect, useMemo, useState } from "react";
import { RedoOutboundLabelsRpcClientContext } from "../outbound-labels-rpc-client";
import { LotBadge } from "../wms/shared/lot-badge";
import * as styles from "./bin-selection-modal.module.css";

export interface ProductBinInfo {
  productId: string;
  productName: string;
  requiredQuantity: number;
  bins: {
    storageLocationId: string;
    binName: string;
    availableQuantity: number;
    inventoryItemId?: string | null;
    lotNumber?: string | null;
    expirationDate?: string | null;
  }[];
}

interface BinSelectionModalProps {
  open: boolean;
  lineItems: ProductBinInfo[];
  fulfillmentGroupIds: string[];
  onConfirm: () => void;
  onCancel: () => void;
  onBypass?: () => void;
  bypassLabel?: string;
}

interface InventoryAdjustmentWarningModalProps {
  open: boolean;
  lineItems: ProductBinInfo[];
  onConfirm: () => void;
  onCancel: () => void;
  onBypass?: () => void;
  bypassLabel?: string;
}

export function useBinSelectionModal() {
  const [state, setState] = useState<{
    open: boolean;
    lineItems: ProductBinInfo[];
    fulfillmentGroupIds: string[];
    onConfirm: () => void;
    onBypass?: () => void;
    bypassLabel?: string;
    mode: "bin-selection" | "inventory-adjustment-warning";
  }>({
    open: false,
    lineItems: [],
    fulfillmentGroupIds: [],
    onConfirm: () => {},
    mode: "bin-selection",
  });

  const openBinSelectionModal = useHandler(
    (params: {
      lineItems: ProductBinInfo[];
      fulfillmentGroupIds: string[];
      onConfirm: () => void;
      onBypass?: () => void;
      bypassLabel?: string;
    }) => {
      setState({ open: true, mode: "bin-selection", ...params });
    },
  );

  const openInventoryAdjustmentWarningModal = useHandler(
    (params: {
      lineItems: ProductBinInfo[];
      onConfirm: () => void;
      onBypass?: () => void;
      bypassLabel?: string;
    }) => {
      setState({
        open: true,
        lineItems: params.lineItems,
        fulfillmentGroupIds: [],
        onConfirm: params.onConfirm,
        onBypass: params.onBypass,
        bypassLabel: params.bypassLabel,
        mode: "inventory-adjustment-warning",
      });
    },
  );

  const modalProps: BinSelectionModalProps = useMemo(
    () => ({
      open: state.open && state.mode === "bin-selection",
      lineItems: state.lineItems,
      fulfillmentGroupIds: state.fulfillmentGroupIds,
      onConfirm: () => {
        setState((s) => ({ ...s, open: false }));
        state.onConfirm();
      },
      onCancel: () => {
        setState((s) => ({ ...s, open: false }));
      },
      onBypass: state.onBypass
        ? () => {
            setState((s) => ({ ...s, open: false }));
            state.onBypass?.();
          }
        : undefined,
      bypassLabel: state.bypassLabel,
    }),
    [state],
  );

  const inventoryAdjustmentWarningModalProps: InventoryAdjustmentWarningModalProps =
    useMemo(
      () => ({
        open: state.open && state.mode === "inventory-adjustment-warning",
        lineItems: state.lineItems,
        onConfirm: () => {
          setState((s) => ({ ...s, open: false }));
          state.onConfirm();
        },
        onCancel: () => {
          setState((s) => ({ ...s, open: false }));
        },
        onBypass: state.onBypass
          ? () => {
              setState((s) => ({ ...s, open: false }));
              state.onBypass?.();
            }
          : undefined,
        bypassLabel: state.bypassLabel,
      }),
      [state],
    );

  return {
    modalProps,
    inventoryAdjustmentWarningModalProps,
    openBinSelectionModal,
    openInventoryAdjustmentWarningModal,
  };
}

function computeDefaultAllocations(
  product: ProductBinInfo,
): Map<string, number> {
  const allocations = new Map<string, number>();
  let remaining = product.requiredQuantity;

  for (const bin of product.bins) {
    if (remaining <= 0) {
      break;
    }
    const qty = Math.min(remaining, bin.availableQuantity);
    allocations.set(bin.storageLocationId, qty);
    remaining -= qty;
  }

  return allocations;
}

export const BinSelectionModal = memo(function BinSelectionModal({
  open,
  lineItems,
  fulfillmentGroupIds,
  onConfirm,
  onCancel,
  onBypass,
  bypassLabel = "Bypass",
}: BinSelectionModalProps) {
  const client = useRequiredContext(RedoOutboundLabelsRpcClientContext);

  // Map<productId, Map<storageLocationId, quantity>>
  const [allocations, setAllocations] = useState<
    Map<string, Map<string, number>>
  >(new Map());

  // Track which bins are visible per product (initialized from default allocations)
  const [visibleBins, setVisibleBins] = useState<Map<string, Set<string>>>(
    new Map(),
  );

  useEffect(() => {
    if (!open) {
      return;
    }
    const initialAllocations = new Map<string, Map<string, number>>();
    const initialVisible = new Map<string, Set<string>>();
    for (const item of lineItems) {
      const defaults = computeDefaultAllocations(item);
      initialAllocations.set(item.productId, defaults);
      initialVisible.set(item.productId, new Set(defaults.keys()));
    }
    setAllocations(initialAllocations);
    setVisibleBins(initialVisible);
  }, [open, lineItems]);

  const updateAllocation = (
    productId: string,
    storageLocationId: string,
    quantity: number,
  ) => {
    setAllocations((prev) => {
      const next = new Map(prev);
      const productAllocs = new Map(next.get(productId) ?? new Map());
      if (quantity <= 0) {
        productAllocs.delete(storageLocationId);
      } else {
        productAllocs.set(storageLocationId, quantity);
      }
      next.set(productId, productAllocs);
      return next;
    });
  };

  const getProductTotal = (productId: string): number => {
    const productAllocs = allocations.get(productId);
    if (!productAllocs) {
      return 0;
    }
    let total = 0;
    for (const qty of productAllocs.values()) {
      total += qty;
    }
    return total;
  };

  const addBin = useHandler((productId: string, storageLocationId: string) => {
    setVisibleBins((prev) => {
      const next = new Map(prev);
      const bins = new Set(next.get(productId));
      bins.add(storageLocationId);
      next.set(productId, bins);
      return next;
    });
  });

  const removeBin = useHandler(
    (productId: string, storageLocationId: string) => {
      setVisibleBins((prev) => {
        const next = new Map(prev);
        const bins = new Set(next.get(productId));
        bins.delete(storageLocationId);
        next.set(productId, bins);
        return next;
      });
      updateAllocation(productId, storageLocationId, 0);
    },
  );

  const isValid = lineItems.every(
    (item) => getProductTotal(item.productId) === item.requiredQuantity,
  );

  const [submitLoad, triggerSubmit] = useTriggerLoad(async () => {
    const binSelections: Record<
      string,
      { storageLocationId: StorageLocationId; quantity: number }[]
    > = {};

    for (const [productId, productAllocs] of allocations.entries()) {
      const selections: {
        storageLocationId: StorageLocationId;
        quantity: number;
      }[] = [];
      for (const [storageLocationId, quantity] of productAllocs.entries()) {
        if (quantity > 0) {
          selections.push({
            storageLocationId:
              storageLocationIdFormat.schema.parse(storageLocationId),
            quantity,
          });
        }
      }
      if (selections.length > 0) {
        binSelections[productId] = selections;
      }
    }

    try {
      await client.recordDirectBinPicks({ fulfillmentGroupIds, binSelections });
      onConfirm();
    } catch (error) {
      toast("Failed to record bin selection", { variant: "error" });
    }
  });

  if (lineItems.length === 0) {
    return null;
  }

  return (
    <RedoModal
      hideFooter
      isOpen={open}
      modalSize="md"
      onModalCloseRequested={onCancel}
      subtitle={`Select which bins to pull items from for ${
        fulfillmentGroupIds.length === 1 ? "this shipment" : "these shipments"
      }`}
      theme="brand"
      title="Select bin locations"
    >
      <Flex dir="column" gap="xl" pb="3xl">
        {lineItems.map((item) => {
          const productTotal = getProductTotal(item.productId);
          const productAllocs = allocations.get(item.productId) ?? new Map();
          const productVisible =
            visibleBins.get(item.productId) ?? new Set<string>();

          return (
            <Flex dir="column" gap="md" key={item.productId}>
              <Flex align="center" dir="row" justify="space-between">
                <Text fontWeight="semibold">{item.productName}</Text>
                <Text
                  fontSize="xs"
                  textColor={
                    productTotal === item.requiredQuantity ? "primary" : "error"
                  }
                >
                  {productTotal} / {item.requiredQuantity}
                </Text>
              </Flex>

              {item.bins.length === 0 ? (
                <Text fontSize="xs" textColor="tertiary">
                  No bins available for this product
                </Text>
              ) : (
                <>
                  {item.bins
                    .filter((bin) => productVisible.has(bin.storageLocationId))
                    .map((bin) => (
                      <Flex
                        align="center"
                        dir="row"
                        gap="md"
                        key={bin.storageLocationId}
                      >
                        <Flex dir="column" grow={1}>
                          <Flex align="center" dir="row" gap="xs">
                            <Text fontSize="sm">{bin.binName}</Text>
                            {bin.lotNumber && (
                              <LotBadge
                                expirationDate={bin.expirationDate}
                                lotNumber={bin.lotNumber}
                              />
                            )}
                          </Flex>
                          <Text fontSize="xxs" textColor="quaternary">
                            Available: {bin.availableQuantity}
                          </Text>
                        </Flex>
                        <Flex className={styles.quantityInput}>
                          <RedoTextInput
                            inputTypeHint="number"
                            max={bin.availableQuantity}
                            min={0}
                            setValue={(val) => {
                              const num = parseInt(val, 10);
                              updateAllocation(
                                item.productId,
                                bin.storageLocationId,
                                isNaN(num)
                                  ? 0
                                  : Math.min(num, bin.availableQuantity),
                              );
                            }}
                            size="sm"
                            value={String(
                              productAllocs.get(bin.storageLocationId) ?? 0,
                            )}
                          />
                        </Flex>
                        <RedoButton
                          aria-label="Remove"
                          hierarchy="tertiary"
                          IconLeading={XClose}
                          onClick={() =>
                            removeBin(item.productId, bin.storageLocationId)
                          }
                          size="xs"
                        />
                      </Flex>
                    ))}
                  {(() => {
                    const hiddenBins = item.bins.filter(
                      (bin) => !productVisible.has(bin.storageLocationId),
                    );
                    if (hiddenBins.length === 0) {
                      return null;
                    }
                    const options: RedoListItem<string>[] = hiddenBins.map(
                      (bin) => ({
                        id: bin.storageLocationId,
                        value: bin.storageLocationId,
                        type: "text" as const,
                        text: bin.lotNumber
                          ? `${bin.binName} · ${bin.lotNumber}${bin.expirationDate ? ` · Exp ${bin.expirationDate}` : ""}`
                          : bin.binName,
                        supportingText: `Available: ${bin.availableQuantity}`,
                      }),
                    );
                    return (
                      <RedoSingleSelectDropdownInput
                        options={options}
                        optionSelected={({ item: selected }) => {
                          addBin(item.productId, selected.value);
                        }}
                        placeholder="Add bin..."
                        size="xs"
                      />
                    );
                  })()}
                </>
              )}
            </Flex>
          );
        })}

        <Flex align="center" dir="row" gap="md" justify="flex-end" pt="lg">
          {onBypass && (
            <RedoButton
              hierarchy="tertiary"
              onClick={onBypass}
              size="md"
              text={bypassLabel}
              theme="normal"
            />
          )}
          <RedoButton
            hierarchy="secondary"
            onClick={onCancel}
            size="md"
            text="Cancel"
            theme="normal"
          />
          <RedoButton
            disabled={!isValid || submitLoad.pending}
            hierarchy="primary"
            onClick={() => triggerSubmit()}
            pending={submitLoad.pending}
            size="md"
            text="Confirm and buy label"
            theme="normal"
          />
        </Flex>
      </Flex>
    </RedoModal>
  );
});

export const InventoryAdjustmentWarningModal = memo(
  function InventoryAdjustmentWarningModal({
    open,
    lineItems,
    onConfirm,
    onCancel,
    onBypass,
    bypassLabel = "Bypass",
  }: InventoryAdjustmentWarningModalProps) {
    if (!lineItems.length) {
      return null;
    }

    return (
      <RedoModal
        hideFooter={!!onBypass}
        isOpen={open}
        modalSize="sm"
        onModalCloseRequested={onCancel}
        primaryButton={
          onBypass
            ? undefined
            : { text: "Continue with inventory adjustment", onClick: onConfirm }
        }
        secondaryButton={
          onBypass ? undefined : { text: "Cancel", onClick: onCancel }
        }
        theme="warn"
        title="No stock found in storage locations"
        TitleIcon={AlertTriangleIcon}
      >
        <Flex dir="column" gap="md">
          <Text>
            We could not find stock for these items in any media or storage
            location. Continuing will fulfill this shipment with an inventory
            adjustment instead of a warehouse transfer.
          </Text>
          <Flex dir="column" gap="xs">
            {lineItems.map((item) => (
              <Text fontSize="xs" key={item.productId} textColor="tertiary">
                {item.productName}: {item.requiredQuantity}
              </Text>
            ))}
          </Flex>
          {onBypass && (
            <Flex align="center" dir="row" gap="md" justify="flex-end" pt="lg">
              {onBypass && (
                <RedoButton
                  hierarchy="tertiary"
                  onClick={onBypass}
                  size="md"
                  text={bypassLabel}
                  theme="normal"
                />
              )}
              <RedoButton
                hierarchy="secondary"
                onClick={onCancel}
                size="md"
                text="Cancel"
                theme="normal"
              />
              <RedoButton
                hierarchy="primary"
                onClick={onConfirm}
                size="md"
                text="Continue with inventory adjustment"
                theme="normal"
              />
            </Flex>
          )}
        </Flex>
      </RedoModal>
    );
  },
);
